<template>
  <Dialog
    :visible="visible"
    :title="$t(`${namespace}.categoryPermissionBulkTitle`)"
    :isLoading="isLoading"
    max-width="638"
    @close="close()"
  >
    <div v-if="visible">
      <form novalidate @submit.prevent="batchUpdate">
        <v-card-text class="dialog__body">
          <p class="pb-2">{{ $t(`${namespace}.categoryPermissionBulkMsg`) }}</p>
          <p><strong>{{ $t(`${namespace}.categoryPermissionBulkStep1`) }}</strong></p>

          <v-radio-group
            v-model="radioGroup"
            v-validate="{ required: true }"
            :error-messages="errors.collect($t('Common.employees'))"
            :data-vv-name="$t('Common.employees')"
            required
          >
            <v-radio :label="$t(`${namespace}.allEmployees`)" value="allEmployees"></v-radio>
            <v-radio :label="$t(`${namespace}.selectEmployeesByName`)" value="perName"></v-radio>
            <v-radio :label="$t(`${namespace}.selectEmployeesById`)" value="perId"></v-radio>
          </v-radio-group>

          <AutoComplete
            v-if="radioGroup === 'perName'"
            kind="employee"
            :data="employeeIds"
            :companyId="company.id"
            :placeholder="$t('Components.ApplyToAllDialog.searchEmployees')"
            :isValid="radioGroup === 'perName' && isEmployeeValid"
            :required="radioGroup === 'perName'"
            multiple
            @input="updateEmployee"
          />

          <FormTextField
            v-if="radioGroup === 'perId'"
            v-model="employeesIdsField"
            :name="$t(`${namespace}.fieldIdsName`)"
            :placeholder="$t(`${namespace}.fieldIdsPlaceholder`)"
            :disabled="isLoading"
            :v-validate="{ required: true }"
            ref="name"
            @blur="normalizeIDs"
          />

          <p class="mb-0"><strong>{{ $t(`${namespace}.categoryPermissionBulkStep2`) }}</strong></p>
        </v-card-text>

        <div class="pb-5">
          <CarCategoriesList
            :categories="categoriesList"
            class="mb-5 mx-2"
            :required="!isCategoryIdValid"
            @getCategoriesList="updateCategoriesList"
          />
        </div>

        <v-divider />

        <v-card-actions class="dialog__footer">
          <v-spacer />

          <a
            v-if="!isLoading"
            class="mr-4"
            @click.prevent="close()"
          >
            {{ $t(`${namespace}.cancel`) }}
          </a>

          <Button
            type="submit"
            :loading="isLoading"
            :disabled="isLoading"
          >
            {{ $t(`${namespace}.categoryPermissionBulkBtn`) }}
          </Button>
        </v-card-actions>
      </form>
    </div>
  </Dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { isValidArray, objectToArray, catchError } from 'corp-front-vue-commons/src/utils/functions'
import AutoComplete from 'components/AutoComplete'
import CarCategoriesList from 'components/pages/employees/CarCategoriesList'

export default {
  name: 'CategoryPermissionBulk',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
      company: ({ company }) => company.selectedCompany
    }),

    categoriesList () {
      return objectToArray(this.$t('Common.ridesCategoriesList')).map(item => item.value)
    }
  },
  components: {
    AutoComplete,
    CarCategoriesList
  },
  data () {
    return {
      namespace: 'Components.ApplyToAllDialog',
      isLoading: false,
      radioGroup: null,

      // custom validation
      isEmployeeValid: true,
      isCategoryIdValid: true,

      // fields
      employeeIds: [],
      employeesIdsField: '',
      categoryIds: []
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    isValidArray (val) {
      return isValidArray(val)
    },

    setLoadingValue (boolean) {
      this.isLoading = boolean
    },

    forceFieldValidation (ref, selector) {
      const $field = this.$refs[ref]?.$el

      if ($field) {
        $field.querySelector(selector).focus()
        $field.querySelector(selector).blur()
      }
    },

    normalizeIDs () {
      if (this.employeesIdsField.trim().length) {
        this.employeesIdsField = this.employeesIdsField
          .replace(/[^\d,]/g, '')
          .replace(/,+/g, ',')

        this.employeeIds = this.employeesIdsField.split(',').map(Number).filter(Boolean)
      }
    },

    resetEmployeeOptions () {
      this.employeeIds = []
      this.employeesIdsField = ''
    },

    resetAllFields () {
      this.resetEmployeeOptions()
      this.isEmployeeValid = true
      this.isCategoryIdValid = true
      this.radioGroup = null
      this.categoryIds = []
    },

    close (reload = false) {
      this.$emit('close', reload)
    },

    updateEmployee (val) {
      if (!this.isValidArray(val)) {
        this.employeeIds = []
        this.radioGroup !== 'allEmployees' && (this.isEmployeeValid = false)
        return
      }

      this.employeeIds = val
      this.radioGroup !== 'allEmployees' && (this.isEmployeeValid = true)
    },

    updateCategoriesList (val) {
      if (!this.isValidArray(val)) {
        this.categoryIds = []
        this.isCategoryIdValid = false
        return
      }

      this.categoryIds = val
      this.isCategoryIdValid = true
    },

    async batchUpdate () {
      if (this.radioGroup !== 'allEmployees' && !this.employeeIds.length) {
        this.isEmployeeValid = false
      }

      if (!this.categoryIds.length) {
        this.isCategoryIdValid = false
      }

      this.forceFieldValidation('name', 'input')

      try {
        const isValid = await this.$validator.validateAll()

        if (this.radioGroup !== 'allEmployees' && !this.employeeIds.length) return

        if (isValid && this.categoryIds.length) {
          this.setLoadingValue(true)

          let ids = null

          if (this.radioGroup === 'perName') {
            const employeeIds = this.employeeIds.map(item => item.id)
            ids = this.employeeIds.length ? employeeIds : null
          } else {
            ids = this.employeeIds
          }

          await this.$apiService.employee.batchUpdate(
            this.company.id,
            { budget: null },
            { budgetDate: null },
            { budgetPeriod: 'monthly' },
            { credit: null },
            { useBudget: null },
            { useRideCredit: null },
            { categoryIds: this.categoryIds },
            { supervisorId: null },
            { thirdPartyWebCallPermission: null },
            { webCallPermission: null },
            { ids }
          )

          this.showAlert({
            type: 'success',
            message: this.$t(`${this.namespace}.categoryPermissionBulkSuccess`)
          })

          this.resetAllFields()
          this.close(true)
          this.setLoadingValue(false)
        }
      } catch (error) {
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.error`),
          timeout: 6000
        })

        this.setLoadingValue(false)
        catchError(error)
      }
    }
  },
  watch: {
    radioGroup () {
      this.resetEmployeeOptions()
    },

    employeesIdsField (val) {
      if (!val) this.resetEmployeeOptions()
    },

    visible (val) {
      // fix validation error when close dialog
      this.$validator._paused = !val

      this.resetAllFields()
      this.setLoadingValue(false)

      val && this.$ga.page('/dialog/colaboradores/permissoes-de-categorias-em-massa')
    }
  }
}
</script>
